import { CheckCircle2, XCircle, X, ArrowRight } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Chip } from '../interviews/Chip'
import { formatDate } from '../../utils/interviews'
import type { BatchApplyResult, BatchCloseResult } from './batch'

// After a batch runs we show this panel — never a toast — because the refused
// rows are the important output: they are the ones that still need a human.
// The panel stays visible until the reviewer dismisses it.
//
// Handles both the apply batch (outcome 'applied' | 'refused', with change
// detail) and the close batch (outcome 'closed' | 'refused'). Every refused row
// is listed with the database's verbatim `detail` message.
export function BatchResultPanel({
  mode,
  applyResults,
  closeResults,
  onDismiss,
}: {
  mode: 'apply' | 'close'
  applyResults: BatchApplyResult[]
  closeResults: BatchCloseResult[]
  onDismiss: () => void
}) {
  const succeededWord = mode === 'apply' ? 'applied' : 'closed'

  const successCount =
    mode === 'apply'
      ? applyResults.filter((r) => r.outcome === 'applied').length
      : closeResults.filter((r) => r.outcome === 'closed').length
  const refusedCount =
    mode === 'apply'
      ? applyResults.filter((r) => r.outcome === 'refused').length
      : closeResults.filter((r) => r.outcome === 'refused').length

  const refusedApply = applyResults.filter((r) => r.outcome === 'refused')
  const refusedClose = closeResults.filter((r) => r.outcome === 'refused')

  return (
    <div className="rounded-lg border border-border bg-card shadow-retool-sm">
      <div className="flex items-start justify-between gap-3 border-b border-border p-3">
        <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
          <span className="flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            <span className="tabular-nums">{successCount}</span> {succeededWord}
          </span>
          <span className="text-muted-foreground">·</span>
          <span className="flex items-center gap-1.5">
            <XCircle
              className={
                refusedCount > 0
                  ? 'h-4 w-4 text-rose-600 dark:text-rose-400'
                  : 'h-4 w-4 text-muted-foreground'
              }
            />
            <span className="tabular-nums">{refusedCount}</span> refused
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 px-2 text-xs"
          onClick={onDismiss}
        >
          <X className="h-3.5 w-3.5" />
          Dismiss
        </Button>
      </div>

      {refusedCount === 0 ? (
        <p className="p-3 text-sm text-muted-foreground">
          Every selected correction was {succeededWord}. Nothing needs a second look.
        </p>
      ) : (
        <div className="space-y-2 p-3">
          <p className="text-xs font-medium text-muted-foreground">
            These were refused and still need a human:
          </p>
          <ul className="space-y-2">
            {mode === 'apply'
              ? refusedApply.map((r) => (
                  <li
                    key={r.correction_id}
                    className="rounded-md border border-rose-300 bg-rose-50 p-2.5 text-sm dark:border-rose-800 dark:bg-rose-950"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-foreground">
                        {formatDate(r.shift_date)}
                      </span>
                      <span className="flex items-center gap-1.5 font-mono text-xs text-foreground">
                        {r.from_value || '—'}
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        {r.to_value || '—'}
                      </span>
                      {r.correction_type ? (
                        <Chip tone={r.correction_type === 'material' ? 'red' : 'neutral'}>
                          {r.correction_type}
                        </Chip>
                      ) : null}
                    </div>
                    <p className="mt-1 text-xs leading-relaxed text-rose-800 dark:text-rose-200">
                      {r.detail || 'No reason was returned by the database.'}
                    </p>
                  </li>
                ))
              : refusedClose.map((r) => (
                  <li
                    key={r.correction_id}
                    className="rounded-md border border-rose-300 bg-rose-50 p-2.5 text-sm dark:border-rose-800 dark:bg-rose-950"
                  >
                    <p className="font-mono text-[11px] text-muted-foreground">
                      {r.correction_id}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-rose-800 dark:text-rose-200">
                      {r.detail || 'No reason was returned by the database.'}
                    </p>
                  </li>
                ))}
          </ul>
        </div>
      )}
    </div>
  )
}
